import isFunction from "lodash-es/isFunction";
import values from "lodash-es/values";
import {
  allPermissionsData,
  type PermissionsEnum,
} from "~/constants/access_control";

export const currentUserPermissions = computed(() => {
  const sessionStore = useSessionStore();
  return values(sessionStore.userData?.role?.permissions ?? []);
});

export const userHasPermission = computed(
  () =>
    (
      permission:
        | keyof typeof PermissionsEnum
        | (typeof PermissionsEnum)[keyof typeof PermissionsEnum],
    ) => {
      if (userIsSuperAdmin.value) {
        return true;
      }

      const platformPermissionData = allPermissionsData.find(
        (platformPermission) =>
          platformPermission.slug === permission ||
          platformPermission.name === permission,
      );

      if (!platformPermissionData) {
        return false;
      }

      return currentUserPermissions.value.includes(platformPermissionData.slug);
    },
);

export const userHasSomePermissions = computed(
  () =>
    (
      perms: (
        | keyof typeof PermissionsEnum
        | (typeof PermissionsEnum)[keyof typeof PermissionsEnum]
      )[],
    ) => {
      const userPermissions = currentUserPermissions.value;
      return userPermissions.length
        ? perms.some((perm) => userHasPermission.value(perm))
        : false;
    },
);

export const userHasAllPermissions = computed(
  () =>
    (
      perms: (
        | keyof typeof PermissionsEnum
        | (typeof PermissionsEnum)[keyof typeof PermissionsEnum]
      )[],
    ) => {
      const userPermissions = currentUserPermissions.value;
      return userPermissions.length
        ? perms.every((perm) => userHasPermission.value(perm))
        : false;
    },
);

export function permissionGuard<F extends (...args: unknown[]) => unknown>(
  perms: (
    | keyof typeof PermissionsEnum
    | (typeof PermissionsEnum)[keyof typeof PermissionsEnum]
  )[],
  action: F,
): F;
export function permissionGuard<F extends (...args: unknown[]) => unknown>(
  perms: (
    | keyof typeof PermissionsEnum
    | (typeof PermissionsEnum)[keyof typeof PermissionsEnum]
  )[],
  action: F,
  ...otherChecks: (boolean | ((...args: unknown[]) => boolean))[]
): () => void;

export function permissionGuard<F extends (...args: unknown[]) => unknown>(
  perms: (
    | keyof typeof PermissionsEnum
    | (typeof PermissionsEnum)[keyof typeof PermissionsEnum]
  )[],

  action: F,
  ...otherChecks: (boolean | ((...args: unknown[]) => boolean))[]
): F | (() => void) {
  const toast = useToast();
  const permissionErrorToast = () => {
    toast.error("You do not have permission to perform this action", {
      id: "no-permission",
      timeout: 2000,
    });
  };
  const invalidActionToast = () => {
    toast.error("Invalid action", {
      id: "invalid-action",
      timeout: 2000,
    });
  };

  if (userHasSomePermissions.value(perms) || perms.length === 0) {
    for (const check of otherChecks) {
      if (isFunction(check)) {
        if (!check()) return invalidActionToast;
      } else if (!check) return invalidActionToast;
    }
    return action;
  }
  return permissionErrorToast;
}

export const userIsSuperAdmin = computed(() => {
  const sessionStore = useSessionStore();
  return (
    sessionStore.userData?.companyUser?.isAdmin ||
    sessionStore.userData?.company?.isAdmin ||
    sessionStore.userData?.role?.defaultRole === "super_admin" ||
    false
  );
});

export const userIsPrimeUser = computed(() => {
  const sessionStore = useSessionStore();
  return sessionStore.userData?.role?.defaultRole === "prime_user";
});

export function userBelongsToDepartment(departmentId: string) {
  const sessionStore = useSessionStore();
  return (
    sessionStore.userData?.companyUser?.departmentMember?.department.id ===
      departmentId || userIsSuperAdmin.value
  );
}

export const userIsManager = computed(() => {
  const sessionStore = useSessionStore();
  return sessionStore.userData?.role?.defaultRole === "manager";
});

export function useAccessControl() {
  return {
    currentUserPermissions,
    userHasPermission,
    userHasSomePermissions,
    userHasAllPermissions,
    actionGuard: permissionGuard,
  };
}
